import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { authApi } from '@/api/auth/auth-api'
import {
  toChangePasswordPayload,
  toCurrentUser,
  toUpdateMePayload,
  type ChangePasswordInput,
  type ProfileNameInput,
} from '@/api/auth/auth-mapper'
import { useChatStore } from '@/features/chat/store/chat-store'
import { HttpError } from '@/lib/http/http-error'
import type {
  ConfirmEmailChangeRequestDto,
  EmailChangeRequestDto,
  ForgotPasswordRequestDto,
  LoginRequestDto,
  SetPasswordRequestDto,
} from '@/types/auth/auth.types'
import type { CurrentUser } from '@/types/auth/current-user'

export const authKeys = {
  all: ['auth'] as const,
  me: () => [...authKeys.all, 'me'] as const,
}

export function useCurrentUserQuery() {
  return useQuery({
    queryKey: authKeys.me(),
    queryFn: async ({ signal }): Promise<CurrentUser | null> => {
      try {
        return toCurrentUser(await authApi.me(signal))
      } catch (error) {
        if (error instanceof HttpError && error.status === 401) return null
        throw error
      }
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnReconnect: false,
  })
}

export function useCurrentUser(): CurrentUser | null | undefined {
  return useCurrentUserQuery().data
}

export function useLoginMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: LoginRequestDto) => authApi.login(payload),
    onSuccess: (response) => {
      queryClient.setQueryData(authKeys.me(), toCurrentUser(response))
    },
  })
}

export function endSession(queryClient: QueryClient): void {
  useChatStore.getState().reset()
  queryClient.setQueryData(authKeys.me(), null)
  queryClient.removeQueries({
    predicate: (query) => query.queryKey[0] !== authKeys.all[0],
  })
}

export function useLogoutMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await authApi.logout().catch(() => undefined)
      endSession(queryClient)
    },
  })
}

export function useForgotPasswordMutation() {
  return useMutation({
    mutationFn: (payload: ForgotPasswordRequestDto) => authApi.forgotPassword(payload),
  })
}

export function useSetPasswordMutation() {
  return useMutation({
    mutationFn: (payload: SetPasswordRequestDto) => authApi.setPassword(payload),
  })
}

export function useUpdateMeMutation() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (changes: Partial<ProfileNameInput>) =>
      toCurrentUser(await authApi.updateMe(toUpdateMePayload(changes))),
    onSuccess: (user) => {
      // A password change ends the session and nulls this entry. A name save
      // still in flight when that happens must not put the user back.
      queryClient.setQueryData(
        authKeys.me(),
        (current: CurrentUser | null | undefined) => (current == null ? current : user),
      )
    },
  })
}

export function useChangePasswordMutation() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) =>
      authApi.changePassword(toChangePasswordPayload(input)),
  })
}

export function useRequestEmailChangeMutation() {
  return useMutation({
    mutationFn: (payload: EmailChangeRequestDto) => authApi.requestEmailChange(payload),
  })
}

/**
 * Whether the link behind `?token=` can still set a password, asked before the
 * form is shown so a replaced or spent link says so up front instead of after
 * someone types a password. The answer is pinned for the life of the page: the
 * token never changes, and a refetch mid-typing would pull the form away.
 */
export function useValidatePasswordLinkQuery(token: string | null) {
  return useQuery({
    queryKey: [...authKeys.all, 'password-link', token] as const,
    queryFn: ({ signal }) => authApi.validatePasswordLink(token as string, signal),
    enabled: token !== null,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}

/**
 * The confirmation outcome lives in the query cache, keyed by the token, rather
 * than in a mutation: a token is single-use, and StrictMode remounts the page
 * with a fresh mutation observer while any ref guarding the post survives. That
 * combination spends the token on the first mount and leaves the second one
 * idle forever. Keyed by the token, the request is deduped and its answer is
 * still there after the remount.
 */
export function useConfirmEmailChangeQuery(token: string | null) {
  const queryClient = useQueryClient()
  return useQuery({
    queryKey: [...authKeys.all, 'email-change-confirm', token] as const,
    queryFn: async () => {
      const payload: ConfirmEmailChangeRequestDto = { token: token as string }
      const response = await authApi.confirmEmailChange(payload)
      await queryClient.invalidateQueries({ queryKey: authKeys.me() })
      return response
    },
    enabled: token !== null,
    retry: false,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })
}
